import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '@/services/apiService';

/** Used when the API's filename is unreadable (a CORS-stripped header). */
const FALLBACK_FILENAME = 'polis.pdf';

export interface UsePolicyDocumentDownloadResult {
	download: () => void;
	isDownloading: boolean;
	/** Reader-facing message, set when the download failed. */
	error: string | null;
}

/** Offers an already-fetched file to the browser as a save. */
const saveAs = (blob: Blob, filename: string): void => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	// Revoking in the same tick can cancel the save in some browsers.
	setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * Downloads the policy document the customer uploaded, for the dossier page.
 *
 * The endpoint is bearer-authenticated, so a plain link cannot reach it: the
 * file has to come down through `fetch` and be handed to the browser from an
 * object URL.
 */
const usePolicyDocumentDownload = (id: string | undefined): UsePolicyDocumentDownloadResult => {
	const { t } = useTranslation();
	const [isDownloading, setIsDownloading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const download = useCallback(() => {
		if (!id || isDownloading) return;
		setIsDownloading(true);
		setError(null);
		apiService.policy
			.downloadDocument(id)
			.then(({ blob, filename }) => {
				saveAs(blob, filename ?? FALLBACK_FILENAME);
			})
			.catch(() => {
				setError(t('requestOverview.policyDocument.downloadError'));
			})
			.finally(() => {
				setIsDownloading(false);
			});
	}, [id, isDownloading, t]);

	return { download, isDownloading, error };
};

export default usePolicyDocumentDownload;
